import { moduleNames } from '@va/constants';
import {
  useApiKeysCount,
  useEventsCount,
  useFunnelsCount,
  useHeatmapsCount,
  usePollsCount,
  useRecordingsCount,
  useSurveysCount,
} from '@va/dashboard/api-client/FeatureCards';
import { getApiRequest } from '@va/dashboard/selectors/api';
import { isModuleEnabled as isModuleEnabledSelector } from '@va/dashboard/selectors/app';
import { selectWebsiteMonthlyVisits } from '@va/dashboard/selectors/core';
import { useMutateOnRefresh } from '@va/dashboard/util-hooks';
import { is3asApp } from '@va/util/helpers';
import { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

type LimitType = {
  limit: number;
};

type FeatureCardDataType = {
  count: number;
  limit: number;
  disabled?: boolean;
};

export type LimitDataType = {
  eventTracking?: LimitType;
  funnels?: LimitType;
  heatmaps?: LimitType;
  polls?: LimitType;
  recordings?: LimitType;
  surveys?: LimitType;
  pageVisits?: LimitType;
  isPayable?: boolean;
  unlimitedVoucher?: unknown;
  wixCycleType?: number;
  wixPermissionPack?: number;
  [key: string]: unknown;
};

type ContextDataType = {
  isRefreshing: boolean;
  limits?: LimitDataType;
  polls: FeatureCardDataType & { mutate: () => void };
  eventTracking: FeatureCardDataType;
  recordings: FeatureCardDataType & { mutate: () => void };
  surveys: FeatureCardDataType & { mutate: () => void };
  heatmaps: FeatureCardDataType & { mutate: () => void };
  funnels: FeatureCardDataType & { mutate: () => void };
  pageVisits: FeatureCardDataType;
  apiKeys: FeatureCardDataType & { mutate: () => void };
};

const FeatureCounterContext = createContext<ContextDataType>({} as ContextDataType);

type Features = {
  surveys: boolean;
  polls: boolean;
  funnels: boolean;
  heatmaps: boolean;
  events: boolean;
  recordings: boolean;
  visits: boolean;
  apiKeys: boolean;
};

const FeatureCounterContextProvider: React.FC<PropsWithChildren<{ features?: Partial<Features> }>> = ({
  children,
  features,
}) => {
  const visitsCount = useSelector(selectWebsiteMonthlyVisits);

  const isModuleEnabled = useSelector(isModuleEnabledSelector);
  const limits: any = useSelector((state: {}) => getApiRequest(state, 'getFeaturePermissions'));

  const { data: surveysCount, mutate: mutateSurveysCount } = useSurveysCount(
    features?.surveys ?? isModuleEnabled(moduleNames.surveys),
  );
  const { data: pollsCount, mutate: mutatePollsCount } = usePollsCount(
    features?.polls ?? isModuleEnabled(moduleNames.polls),
  );
  const { data: funnelsCount, mutate: mutateFunnelsCount } = useFunnelsCount(
    features?.funnels ?? isModuleEnabled(moduleNames.funnels),
  );
  const { data: heatmapsCount, mutate: mutateHeatmapsCount } = useHeatmapsCount(
    features?.heatmaps ?? isModuleEnabled(moduleNames.heatmaps),
  );
  const { data: eventsCount, mutate: mutateEventsCount } = useEventsCount(
    !is3asApp() && (features?.events ?? isModuleEnabled(moduleNames.eventTracking)),
  );
  const { data: recordingsCount, mutate: mutateRecordingsCount } = useRecordingsCount(
    features?.recordings ?? isModuleEnabled(moduleNames.sessionRecordings),
  );

  const { data: apiKeysCount, mutate: mutateApiKeysCount } = useApiKeysCount(
    features?.apiKeys ?? isModuleEnabled(moduleNames.apiKeys),
  );

  const mutateData = useCallback(() => {
    mutatePollsCount();
    mutateFunnelsCount();
    mutateSurveysCount();
    mutateHeatmapsCount();
    mutateEventsCount();
    mutateRecordingsCount();
    mutateApiKeysCount();
  }, [
    mutateApiKeysCount,
    mutateEventsCount,
    mutateFunnelsCount,
    mutateHeatmapsCount,
    mutatePollsCount,
    mutateRecordingsCount,
    mutateSurveysCount,
  ]);

  const { isManualRefreshing } = useMutateOnRefresh(mutateData);

  // VISA-9571 Set 3as limits to Infinity as they are stp-based instead of plan-based as for other platforms
  if (is3asApp()) {
    return (
      <FeatureCounterContext.Provider
        value={{
          isRefreshing: isManualRefreshing,
          polls: {
            count: pollsCount ?? 0,
            limit: Infinity,
            mutate: mutatePollsCount,
            disabled: !isModuleEnabled(moduleNames.polls),
          },
          eventTracking: {
            count: eventsCount ?? 0,
            limit: Infinity,
            disabled: !isModuleEnabled(moduleNames.eventTracking),
          },
          recordings: {
            count: recordingsCount ?? 0,
            limit: Infinity,
            mutate: mutateRecordingsCount,
            disabled: !isModuleEnabled(moduleNames.sessionRecordings),
          },
          heatmaps: {
            count: heatmapsCount ?? 0,
            limit: Infinity ?? 0,
            mutate: mutateHeatmapsCount,
            disabled: !isModuleEnabled(moduleNames.heatmaps),
          },
          surveys: {
            count: surveysCount ?? 0,
            limit: Infinity,
            mutate: mutateSurveysCount,
            disabled: !isModuleEnabled(moduleNames.surveys),
          },
          funnels: {
            count: funnelsCount ?? 0,
            limit: Infinity,
            mutate: mutateFunnelsCount,
            disabled: !isModuleEnabled(moduleNames.funnels),
          },
          pageVisits: {
            count: visitsCount ?? 0,
            limit: Infinity,
          },
          apiKeys: {
            count: apiKeysCount ?? 0,
            limit: Infinity,
            mutate: mutateApiKeysCount,
            disabled: !isModuleEnabled(moduleNames.apiKeys),
          },
        }}
      >
        {children}
      </FeatureCounterContext.Provider>
    );
  }

  return (
    <FeatureCounterContext.Provider
      value={{
        isRefreshing: isManualRefreshing,
        polls: {
          count: pollsCount ?? 0,
          limit: limits?.polls?.limit ?? 0,
          mutate: mutatePollsCount,
          disabled: !isModuleEnabled(moduleNames.polls),
        },
        eventTracking: {
          count: eventsCount ?? 0,
          limit: limits?.eventTracking?.limit ?? 0,
          disabled: !isModuleEnabled(moduleNames.eventTracking),
        },
        recordings: {
          count: recordingsCount ?? 0,
          limit: limits?.recordings?.limit ?? 0,
          mutate: mutateRecordingsCount,
          disabled: !isModuleEnabled(moduleNames.sessionRecordings),
        },
        heatmaps: {
          count: heatmapsCount ?? 0,
          limit: limits?.heatmaps?.limit ?? 0,
          mutate: mutateHeatmapsCount,
          disabled: !isModuleEnabled(moduleNames.heatmaps),
        },
        surveys: {
          count: surveysCount ?? 0,
          limit: limits?.surveys?.limit ?? 0,
          mutate: mutateSurveysCount,
          disabled: !isModuleEnabled(moduleNames.surveys),
        },
        funnels: {
          count: funnelsCount ?? 0,
          limit: limits?.funnels?.limit ?? 0,
          mutate: mutateFunnelsCount,
          disabled: !isModuleEnabled(moduleNames.funnels),
        },
        pageVisits: {
          count: visitsCount ?? 0,
          limit: limits?.visits?.limit ?? 0,
          disabled: !isModuleEnabled(moduleNames.pageVisits),
        },
        apiKeys: {
          count: apiKeysCount ?? 0,
          limit: limits?.apiKeys?.limit ?? 0,
          mutate: mutateApiKeysCount,
          disabled: !isModuleEnabled(moduleNames.apiKeys),
        },
      }}
    >
      {children}
    </FeatureCounterContext.Provider>
  );
};

const useFeatureCounterContext = () => {
  return useContext(FeatureCounterContext);
};

export { FeatureCounterContextProvider, useFeatureCounterContext };
